import React, { useEffect, useState } from 'react';
import { api } from '../hooks/useAuth';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { TrendingUp, Zap, Snail, AlertCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-3 text-sm shadow-xl">
      <div className="text-slate-400 mb-2 font-medium">{label}</div>
      {payload.map(p => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-400 capitalize">{p.name}:</span>
          <span className="text-white font-semibold">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/overview?days=${days}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-96">
      <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const tti = data?.time_to_sign;

  return (
    <div className="p-6 space-y-6 max-w-screen-xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Analytics</h1>
          <p className="text-slate-500 text-sm mt-0.5">Signing performance & client insights</p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-colors ${
                days === d ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Time-to-sign KPIs */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Avg Time to Sign', value: tti?.avg_hours ? `${Math.round(tti.avg_hours)}h` : '—', icon: Clock, color: 'bg-blue-600' },
          { label: 'Fastest', value: tti?.min_hours ? `${Math.round(tti.min_hours)}h` : '—', icon: Zap, color: 'bg-emerald-600' },
          { label: 'Slowest', value: tti?.max_hours ? `${Math.round(tti.max_hours)}h` : '—', icon: Snail, color: 'bg-amber-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex items-center gap-4">
            <div className={`p-3 rounded-xl ${color} flex-shrink-0`}>
              <Icon size={20} className="text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{value}</div>
              <div className="text-sm text-slate-500">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Volume Chart */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <h2 className="font-semibold text-white mb-6">Daily Document Volume</h2>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data?.daily_volume || []} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <defs>
              <linearGradient id="gradCompleted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradPending" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="date" stroke="#475569" tick={{ fontSize: 11 }} tickFormatter={d => format(new Date(d), 'MMM d')} />
            <YAxis stroke="#475569" tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
            <Area type="monotone" dataKey="completed" stroke="#10b981" fill="url(#gradCompleted)" strokeWidth={2} />
            <Area type="monotone" dataKey="pending" stroke="#3b82f6" fill="url(#gradPending)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Fastest clients */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Zap size={16} className="text-emerald-400" />
            <h2 className="font-semibold text-white">Fastest Signing Clients</h2>
          </div>
          <div className="space-y-3">
            {(data?.fastest_clients || []).length === 0 && (
              <p className="text-slate-500 text-sm">No data yet</p>
            )}
            {(data?.fastest_clients || []).map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-slate-600 text-sm font-mono w-5">{i + 1}</span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">{c.company_name}</div>
                  <div className="text-xs text-slate-500">{c.document_count} doc(s)</div>
                </div>
                <span className="text-emerald-400 font-semibold text-sm">{Math.round(c.avg_hours)}h avg</span>
              </div>
            ))}
          </div>
        </div>

        {/* Slowest clients */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Snail size={16} className="text-amber-400" />
            <h2 className="font-semibold text-white">Slowest Signing Clients</h2>
          </div>
          <div className="space-y-3">
            {(data?.slowest_clients || []).length === 0 && (
              <p className="text-slate-500 text-sm">No data yet</p>
            )}
            {(data?.slowest_clients || []).map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-slate-600 text-sm font-mono w-5">{i + 1}</span>
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">{c.company_name}</div>
                  <div className="text-xs text-slate-500">{c.document_count} doc(s)</div>
                </div>
                <span className="text-amber-400 font-semibold text-sm">{Math.round(c.avg_hours)}h avg</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stuck documents */}
      {(data?.stuck_documents || []).length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle size={16} className="text-red-400" />
            <h2 className="font-semibold text-white">Stuck Documents (&gt;48h pending)</h2>
          </div>
          <div className="space-y-2">
            {data.stuck_documents.map(d => (
              <div key={d.id} className="flex items-center gap-4 bg-red-500/5 border border-red-500/20 rounded-xl px-4 py-3">
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">{d.title}</div>
                  <div className="text-xs text-slate-500">{d.client_name}</div>
                </div>
                <div className="text-red-400 text-sm font-semibold whitespace-nowrap">
                  {Math.round(d.hours_pending)}h pending
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
